import { logger } from '@/lib/logger'
import { getRuntimePublicConfigValue } from '@/src/lib/runtime-public-config'

/**
 * Environment Configuration
 * Centralized access to environment variables with type safety
 */

type Environment = 'development' | 'staging' | 'production'

function getAppUrlFallback(env: Environment): string {
  if (env === 'staging') return 'https://stg.dday.io'
  if (env === 'production') return 'https://dday.io'
  return 'http://localhost:3000'
}

/** Normalize NEXT_PUBLIC_APP_URL to an absolute URL and fail safe to env defaults. */
function normalizeAppUrl(
  rawValue: string | undefined,
  env: Environment
): string {
  const fallback = getAppUrlFallback(env)
  const raw = rawValue?.trim()

  if (!raw) return fallback

  const hasProtocol = /^[a-zA-Z][a-zA-Z\d+\-.]*:\/\//.test(raw)
  const isLocalHost = raw.startsWith('localhost') || raw.startsWith('127.0.0.1')
  const candidate = hasProtocol
    ? raw
    : `${isLocalHost ? 'http' : 'https'}://${raw}`

  try {
    const parsed = new URL(candidate)
    return parsed.toString().replace(/\/$/, '')
  } catch {
    logger.warn(
      `[ENV] Invalid NEXT_PUBLIC_APP_URL "${raw}". Falling back to ${fallback}.`
    )
    return fallback
  }
}

/**
 * Get the current environment
 */
export function getEnvironment(): Environment {
  return (
    (getRuntimePublicConfigValue('NEXT_PUBLIC_ENV') as Environment) ||
    (process.env.NEXT_PUBLIC_ENV as Environment) ||
    'development'
  )
}

/**
 * Get the API base URL
 * In development, auto-detects hostname to support both laptop and mobile testing
 * This ensures cookies work correctly by matching frontend and backend domains
 */
export function getApiUrl(): string {
  const env = getEnvironment()
  // Safe: defaults to false if undefined (staging/production won't have this var)
  const useStageApi =
    getRuntimePublicConfigValue('NEXT_PUBLIC_USE_STAGE_API') === 'true' ||
    process.env.NEXT_PUBLIC_USE_STAGE_API === 'true'

  // Development with stage API flag (only works in development)
  if (env === 'development' && useStageApi) {
    return 'https://stg.api.dday.io'
  }

  // Auto-detect hostname in development for mobile testing
  // This prevents cookie mismatches when switching between laptop and phone
  if (env === 'development' && typeof window !== 'undefined') {
    const hostname = window.location.hostname

    // If accessing via IP (e.g., from phone), use same IP for API
    if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
      return `http://${hostname}:4000`
    }
  }

  // Default to env-specific URL (localhost for development, configured URL for production)
  return (
    getRuntimePublicConfigValue('NEXT_PUBLIC_API_URL') ||
    process.env.NEXT_PUBLIC_API_URL ||
    'http://localhost:4000'
  )
}

/**
 * Get the public app URL used for absolute metadata, canonicals, and sitemaps.
 */
export function getAppUrl(): string {
  const env = getEnvironment()
  const configuredUrl =
    getRuntimePublicConfigValue('NEXT_PUBLIC_APP_URL') ||
    process.env.NEXT_PUBLIC_APP_URL
  return normalizeAppUrl(configuredUrl, env)
}

/**
 * Server-only Search Console token for production metadata verification.
 */
export function getGoogleSiteVerificationCode(): string | undefined {
  return process.env.GOOGLE_SITE_VERIFICATION || undefined
}

/**
 * Check if backend mock is enabled (for offline development without any backend)
 * Safe: defaults to false if undefined (staging/production won't have this var)
 */
export function isBackendMockEnabled(): boolean {
  if (process.env.NODE_ENV === 'production') {
    return false
  }
  return (
    getRuntimePublicConfigValue('NEXT_PUBLIC_USE_BACKEND_MOCK') === 'true' ||
    process.env.NEXT_PUBLIC_USE_BACKEND_MOCK === 'true'
  )
}

/** Host upload concurrency; always 1 so uploads run one-at-a-time (order preserved, UI deterministic). */
export function getDirectUploadHostConcurrency(): number {
  const serializedDefault = 1
  const raw =
    getRuntimePublicConfigValue('NEXT_PUBLIC_DIRECT_UPLOAD_HOST_CONCURRENCY') ??
    process.env.NEXT_PUBLIC_DIRECT_UPLOAD_HOST_CONCURRENCY

  const parsed = Number.parseInt(raw ?? '', 10)
  if (Number.isFinite(parsed) && parsed >= 1) {
    return Math.min(parsed, serializedDefault)
  }

  return serializedDefault
}

/**
 * Check if we're in development
 */
export function isDevelopment(): boolean {
  return getEnvironment() === 'development'
}

/**
 * Check if we're in staging
 */
export function isStaging(): boolean {
  return getEnvironment() === 'staging'
}

/**
 * Check if we're in production
 */
export function isProduction(): boolean {
  return getEnvironment() === 'production'
}

/**
 * Get the CDN URL for the current environment
 * Used for serving static assets (images, lottie files, etc.)
 *
 * Configure via NEXT_PUBLIC_CDN_URL in each environment's .env file:
 * - .env.development: NEXT_PUBLIC_CDN_URL=https://cdn.dev.dday.io
 * - .env.staging:     NEXT_PUBLIC_CDN_URL=https://cdn.stg.dday.io
 * - .env.production:  NEXT_PUBLIC_CDN_URL=https://cdn.dday.io
 */
export function getCdnUrl(): string {
  const cdnUrl =
    getRuntimePublicConfigValue('NEXT_PUBLIC_CDN_URL') ||
    process.env.NEXT_PUBLIC_CDN_URL

  if (!cdnUrl) {
    logger.warn(
      '[ENV] NEXT_PUBLIC_CDN_URL is not set. Assets may fail to load.'
    )
    return ''
  }

  return cdnUrl
}

/**
 * Get full asset URL from CDN
 * @param assetPath - The asset path (e.g., '/assets/vendors/visa.svg')
 * @returns Full CDN URL for the asset
 */
export function getAssetUrl(assetPath: string): string {
  const baseUrl = getCdnUrl()
  // Ensure path starts with / for consistency
  const normalizedPath = assetPath.startsWith('/') ? assetPath : `/${assetPath}`
  return `${baseUrl}${normalizedPath}`
}

/**
 * Get all environment info (useful for debugging)
 */
export function getEnvInfo() {
  const useStageApi =
    getRuntimePublicConfigValue('NEXT_PUBLIC_USE_STAGE_API') === 'true' ||
    process.env.NEXT_PUBLIC_USE_STAGE_API === 'true'

  return {
    environment: getEnvironment(),
    appUrl: getAppUrl(),
    apiUrl: getApiUrl(),
    cdnUrl: getCdnUrl(),
    useBackendMock: isBackendMockEnabled(),
    useStageApi,
  }
}

// ============================================================================
// 📌 VERSION & BUILD INFO
// ============================================================================

/**
 * Get the app version (from package.json, injected at build time)
 * Update version using: npm version patch|minor|major
 * Format: 1.0.0, 1.2.3, etc.
 */
export function getAppVersion(): string {
  return (
    getRuntimePublicConfigValue('NEXT_PUBLIC_APP_VERSION') ||
    process.env.NEXT_PUBLIC_APP_VERSION ||
    'dev'
  )
}

/**
 * Get the git commit SHA (injected at build time)
 * Full 40-character SHA from Vercel's VERCEL_GIT_COMMIT_SHA
 */
export function getGitSha(): string {
  return (
    getRuntimePublicConfigValue('NEXT_PUBLIC_GIT_SHA') ||
    process.env.NEXT_PUBLIC_GIT_SHA ||
    'local-dev'
  )
}

/**
 * Get shortened git commit SHA (first 7 characters)
 * Useful for display in footer/UI
 */
export function getGitShaShort(): string {
  const sha = getGitSha()
  return sha === 'local-dev' ? 'local' : sha.slice(0, 7)
}

/**
 * Get the build ID (generated at build time)
 * Format: YYYYMMDD.HHMMSS-gitsha (e.g., 20260128.143022-abc1234)
 * - Timestamp is UTC
 * - Useful for identifying specific builds, even for the same commit
 */
export function getBuildId(): string {
  return (
    getRuntimePublicConfigValue('NEXT_PUBLIC_BUILD_ID') ||
    process.env.NEXT_PUBLIC_BUILD_ID ||
    'local-dev'
  )
}

/**
 * Get combined version info object
 * Used by /_health endpoint and for debugging
 */
export function getVersionInfo() {
  return {
    version: getAppVersion(),
    buildId: getBuildId(),
    gitSha: getGitSha(),
    gitShaShort: getGitShaShort(),
    environment: getEnvironment(),
  }
}

/**
 * Get display string for version (e.g., "v1.0.0 (abc1234)")
 * Useful for footer or debug displays
 */
export function getVersionDisplay(): string {
  const version = getAppVersion()
  const sha = getGitShaShort()

  if (version === 'dev' && sha === 'local') {
    return 'dev'
  }

  return `${version} (${sha})`
}
